/**
 * A PARADA PROGRAMADA (migração 0089, módulo 05, ferramenta 4): a janela em
 * que a máquina sai da operação, combinada entre a manutenção e a operação.
 *
 * O que as telas DIZEM dela mora aqui, fora do componente, onde um teste
 * alcança — a mesa e o celular escrevem a mesma frase.
 */
import { formatarDataHora, interpretarDataHora } from '@/lib/formato';

/** Onde a parada está em relação a agora. A O.S. liberada não chega aqui. */
export type MomentoDaParada = 'programada' | 'em_curso' | 'atrasada';

export const ROTULO_DO_MOMENTO: Readonly<Record<MomentoDaParada, string>> = {
  programada: 'Parada programada',
  em_curso: 'Na janela de parada',
  /* Passou do fim combinado e a O.S. não liberou: a operação esperava a
     máquina de volta. É atraso, e não passado. */
  atrasada: 'Passou da volta combinada',
};

export function momentoDaParada(de: Date, ate: Date, agora: Date = new Date()): MomentoDaParada {
  if (agora.getTime() >= ate.getTime()) return 'atrasada';
  if (agora.getTime() >= de.getTime()) return 'em_curso';
  return 'programada';
}

const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/**
 * "08/10/2026 · 07:00 às 17:00" no mesmo dia; "08/10/2026 · 07:00 até
 * 09/10/2026 · 17:00" quando atravessa a noite.
 */
export function textoDaJanela(de: Date, ate: Date): string {
  const mesmoDia =
    de.getFullYear() === ate.getFullYear() && de.getMonth() === ate.getMonth() && de.getDate() === ate.getDate();
  return mesmoDia
    ? `${formatarDataHora(de)} às ${HORA.format(ate)}`
    : `${formatarDataHora(de)} até ${formatarDataHora(ate)}`;
}

/**
 * A frase curta, de uma linha, para a fila e a ficha: "Para em 08/10/2026 ·
 * 07:00", "Na janela até 08/10/2026 · 17:00", "Devia voltar em 08/10/2026 ·
 * 17:00".
 */
export function linhaCurtaDaParada(de: Date, ate: Date, agora: Date = new Date()): string {
  const momento = momentoDaParada(de, ate, agora);
  if (momento === 'programada') return `Para em ${formatarDataHora(de)}`;
  if (momento === 'em_curso') return `Na janela até ${formatarDataHora(ate)}`;
  return `Devia voltar em ${formatarDataHora(ate)}`;
}

const DIA_MES = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

/**
 * A janela curta da R06 (p.70), sem o ano: "06/10, 13:00 às 17:00" no mesmo
 * dia; "06/10, 22:00 até 07/10, 06:00" quando atravessa a noite. É a da fila
 * da oficina ("Parada 06/10, 13:00 às 17:00") e dos dados-chave da O.S.
 */
export function janelaCurta(de: Date, ate: Date): string {
  const mesmoDia =
    de.getFullYear() === ate.getFullYear() && de.getMonth() === ate.getMonth() && de.getDate() === ate.getDate();
  return mesmoDia
    ? `${DIA_MES.format(de)}, ${HORA.format(de)} às ${HORA.format(ate)}`
    : `${DIA_MES.format(de)}, ${HORA.format(de)} até ${DIA_MES.format(ate)}, ${HORA.format(ate)}`;
}

/** A hora da agenda do dia (p.70): "08:00 às 12:00"; atravessando o dia, com as datas. */
export function horasDaAgenda(de: Date, ate: Date): string {
  const mesmoDia =
    de.getFullYear() === ate.getFullYear() && de.getMonth() === ate.getMonth() && de.getDate() === ate.getDate();
  return mesmoDia ? `${HORA.format(de)} às ${HORA.format(ate)}` : janelaCurta(de, ate);
}

/** "combinada com Ana Souza por Carlos Lima" — os dois lados, quando se sabe. */
export function textoDeQuemCombinou(combinadaCom: string | null, combinadaPor: string | null): string | null {
  if (!combinadaCom) return null;
  return combinadaPor ? `combinada com ${combinadaCom} por ${combinadaPor}` : `combinada com ${combinadaCom}`;
}

export type CampoDaParada = 'de' | 'ate' | 'combinadaComId';

export interface RascunhoDaParada {
  /** Valor de `<input type="datetime-local">`. */
  readonly de: string;
  readonly ate: string;
  readonly combinadaComId: string;
}

/**
 * O que está errado no formulário, campo a campo, com o que fazer. A mesma
 * regra do servidor e do banco (volta depois da saída; os dois lados), dita
 * antes de enviar.
 */
export function problemasDaParada(r: RascunhoDaParada, eu: string | null): Partial<Record<CampoDaParada, string>> {
  const p: Partial<Record<CampoDaParada, string>> = {};
  const de = interpretarDataHora(r.de);
  const ate = interpretarDataHora(r.ate);
  if (!de) p.de = 'Informe quando a máquina sai da operação.';
  if (!ate) p.ate = 'Informe quando a máquina volta para a operação.';
  if (de && ate && ate.getTime() <= de.getTime()) p.ate = 'A volta tem de ser depois da saída.';
  if (!r.combinadaComId) p.combinadaComId = 'Escolha quem da operação combinou a parada.';
  else if (eu !== null && r.combinadaComId === eu) {
    p.combinadaComId = 'Escolha outra pessoa: quem registra pela manutenção é você, e a parada se combina com a operação.';
  }
  return p;
}
